<script lang="ts" setup>
import { computed, ref } from 'vue';
import api from '@/api';
import { DgrIcon, DgrSelectbox } from '@stockmarkteam/donguri-ui';
import Divider from '@/components/common/divider.vue';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import TextTabItem from '@/components/common/molecules/text-tab-item.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import FavoriteThemeSelectItem from '@/components/home/favorite-theme-select-item.vue';
import {
  useGroups,
  useTeamUsers,
  useThemeList,
  useThemeMemberCounts,
  useUserInfo,
} from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const store = useStore();
const emitter = useEmitter();
const feedType = computed(() => store.state.feedType.feedType);
const { createSnackbar } = useSnackbar();

const { data: groups } = useGroups();
const { data: themes, mutate: mutateThemes } = useThemeList(feedType);
const { data: memberCounts, mutate: mutateMemberCounts } =
  useThemeMemberCounts();
const { data: teamUsers } = useTeamUsers();
const { data: userInfo } = useUserInfo();

const activeThemes = computed(() =>
  (themes.value?.themes ?? []).filter(
    t =>
      !t.is_deleted &&
      (t.access_scope !== 'personal' || t.user_id === userInfo.value?.id),
  ),
);
const candidateThemes = computed(() =>
  activeThemes.value.filter(t => !t.is_favorite),
);
const favoriteThemes = computed(() =>
  activeThemes.value
    .filter(t => t.is_favorite)
    .sort((a, b) => a.order - b.order),
);

const groupsWithTheme = computed(() =>
  (groups.value?.groups ?? []).filter(g =>
    activeThemes.value
      .filter(t => t.group_id !== undefined)
      .map(t => t.group_id)
      .includes(g.id),
  ),
);

type TabId = 'search' | 'favorites';

type Tab = {
  id: TabId;
  text: string;
  count?: number;
};

const tabs = computed<Tab[]>(() => [
  {
    id: 'search',
    text: 'テーマを探す',
  },
  {
    id: 'favorites',
    text: 'お気に入りテーマ',
    count: favoriteThemes.value.length,
  },
]);

const selectedTab = ref<TabId>('search');

const ALL_GROUPS_ID = -1;
const selectedGroupId = ref<number>(ALL_GROUPS_ID);
const groupOptions = computed(() => [
  { label: 'すべてのグループ', value: ALL_GROUPS_ID },
  ...groupsWithTheme.value.map(i => ({ label: i.name, value: i.id })),
]);

type ThemeOrder = 'recommended' | 'popular' | 'new';
const selectedOrder = ref<ThemeOrder>('recommended');
const orderOptions = [
  { label: 'おすすめ順', value: 'recommended' as ThemeOrder },
  { label: '人気順', value: 'popular' as ThemeOrder },
  { label: '新着順', value: 'new' as ThemeOrder },
];

const query = ref('');

const getMemberCount = (themeId: number) =>
  memberCounts.value?.theme_member_counts[String(themeId)] ?? 0;

const getCreator = (creatorId?: number | null) =>
  teamUsers.value?.users.find(u => u.id === creatorId);

const filteredThemes = computed(() => {
  const lowerQuery = query.value.toLowerCase();
  return candidateThemes.value.filter(t => {
    if (
      selectedGroupId.value &&
      selectedGroupId.value !== ALL_GROUPS_ID &&
      t.group_id !== selectedGroupId.value
    ) {
      return false;
    }
    return t.name.toLowerCase().includes(lowerQuery);
  });
});

const sortedThemes = computed(() => {
  const list = [...filteredThemes.value];
  if (selectedOrder.value === 'recommended') {
    list
      .sort((a, b) => getMemberCount(b.id) - getMemberCount(a.id))
      .sort((a, b) => (b.is_member ? 1 : 0) - (a.is_member ? 1 : 0));
  } else if (selectedOrder.value === 'popular') {
    list.sort((a, b) => getMemberCount(b.id) - getMemberCount(a.id));
  } else {
    list.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }
  return list;
});

const selectedThemeIds = ref<number[]>([]);
const toggleTheme = (themeId: number) => {
  if (isAddingThemes.value) return;

  if (selectedThemeIds.value.includes(themeId)) {
    selectedThemeIds.value = selectedThemeIds.value.filter(i => i !== themeId);
  } else {
    selectedThemeIds.value.push(themeId);
  }
};
const hasSelectedThemes = computed(() => selectedThemeIds.value.length !== 0);

const isAddingThemes = ref(false);

const addFavoriteThemes = async () => {
  if (isAddingThemes.value) return;

  isAddingThemes.value = true;
  try {
    const selectedThemes = selectedThemeIds.value
      .map(themeId => candidateThemes.value.find(t => t.id === themeId))
      .filter((t): t is (typeof candidateThemes.value)[number] => !!t);

    const hasGroupToJoin = selectedThemes.some(
      theme => !theme.is_member && theme.group_id,
    );

    // 並列に登録すると、追加される順番が選択順と一致しなくなるため直列で登録する
    for (const theme of selectedThemes) {
      const { order } = await api.postFavoriteTheme(theme.id);
      theme.is_member = true;
      theme.is_favorite = true;
      theme.order = order;

      store.commit('themeList/updateTheme', theme);
      emitter.emit('theme-updated', theme);
    }
    if (hasGroupToJoin) {
      emitter.emit('update-group-list');
    }

    selectedThemeIds.value = [];
    await mutateThemes();
    await mutateMemberCounts();
    createSnackbar({
      message: 'お気に入りテーマを登録しました',
      type: 'success',
    });
  } catch (_) {
    createSnackbar({
      message: 'お気に入りテーマを登録できませんでした',
      type: 'error',
    });
  } finally {
    isAddingThemes.value = false;
  }
};

const removeFavoriteTheme = async (themeId: number) => {
  const theme = favoriteThemes.value.find(t => t.id === themeId);
  if (!theme) return;

  try {
    theme.is_favorite = false;
    await api.deleteFavoriteTheme(theme.id);

    store.commit('themeList/updateTheme', theme);
    emitter.emit('theme-updated', theme);
    await mutateThemes();
    createSnackbar({
      message: 'お気に入りテーマから外しました',
      type: 'success',
    });
  } catch (_) {
    createSnackbar({
      message: 'お気に入りテーマから外せませんでした',
      type: 'error',
    });
  }
};
</script>

<template>
  <div class="favorite-theme-select">
    <div class="title">
      <DgrIcon
        name="star-fill"
        :keep-fill="false"
        class="aconnect-home-icon icon"
      />
      <span class="c-title-bold--xm"
        >お気に入りテーマの最新情報を、毎日お届けします</span
      >
    </div>
    <div class="tabs">
      <TextTabItem
        v-for="tab in tabs"
        :key="tab.id"
        :text="tab.text"
        :count="tab.count"
        :is-selected="selectedTab === tab.id"
        @click-tab="selectedTab = tab.id"
      >
      </TextTabItem>
    </div>
    <div v-if="selectedTab === 'search'" class="search-tab">
      <div class="header">
        <div class="c-text-regular--s">
          チームで作られたテーマから、気になるものを選んで登録しましょう。関連するニュース・論文・特許などを毎日まとめてお届けします。
        </div>

        <Divider class="full-bleed" margin="0 -16px" />
        <SearchBar
          v-model="query"
          placeholder="テーマ名を検索"
          :keep-fill="false"
        />
        <div class="filters">
          <div class="group">
            <span class="c-title-bold--s">グループ</span>
            <DgrSelectbox
              :options="groupOptions"
              v-model="selectedGroupId"
              size="small"
            />
          </div>
          <div class="order">
            <span class="c-title-bold--s">並び替え</span>
            <DgrSelectbox
              :options="orderOptions"
              v-model="selectedOrder"
              size="small"
            />
          </div>
        </div>
      </div>
      <div class="themes-list">
        <FavoriteThemeSelectItem
          v-for="theme in sortedThemes"
          :key="theme.id"
          :theme="theme"
          :member-count="getMemberCount(theme.id)"
          :creator="getCreator(theme.creator_id)"
          :icon-name="
            selectedThemeIds.includes(theme.id) ? 'star-fill' : 'star'
          "
          @click="toggleTheme(theme.id)"
        />
      </div>
      <div class="footer">
        <div class="selected-count">
          <span class="c-title-bold--xm">{{ selectedThemeIds.length }}</span
          ><span class="c-text-regular--m">件を選択中</span>
        </div>
        <button
          class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
          :class="{ disabled: !hasSelectedThemes || isAddingThemes }"
          :disabled="!hasSelectedThemes || isAddingThemes"
          @click="addFavoriteThemes"
        >
          お気に入りテーマを登録
        </button>
      </div>
    </div>
    <div v-else class="favorites-tab">
      <div v-if="favoriteThemes.length > 0" class="favorites-content">
        <div class="header">
          <div class="c-text-regular--s">
            毎日のメールでは、登録順の6件までお届けします。7件目以降もAconnect内でいつでも確認できます。
          </div>
        </div>
        <div class="themes-list">
          <FavoriteThemeSelectItem
            v-for="theme in favoriteThemes"
            :key="theme.id"
            :theme="theme"
            :member-count="getMemberCount(theme.id)"
            :creator="getCreator(theme.creator_id)"
            icon-name="star-fill"
            @click="removeFavoriteTheme(theme.id)"
          />
        </div>
        <div class="footer">
          <span class="c-text-regular--s message">
            星（<DgrIcon
              name="star-fill"
              size="small"
              :keep-fill="false"
            />）をクリックするとお気に入りテーマから外せます
          </span>
        </div>
      </div>
      <div v-else class="empty">
        <div class="icon">
          <DgrIcon name="star" :keep-fill="false" />
        </div>
        <div class="c-title-medium--m">まだお気に入りテーマがありません。</div>
        <div class="message">
          <div class="c-text-regular--s">
            お気に入りテーマを追加すると、関連するニュース・論文・特許などの情報を毎日お届けします。
          </div>
          <div class="c-text-regular--s">
            未追加のテーマから、気になるものを探してみましょう。
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.favorite-theme-select {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 680px;
  padding-top: 12px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background: #ffffff;

  .title {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
    padding: 0 16px;
    margin-bottom: 12px;

    .icon {
      color: $color-green600;
    }
  }
  .tabs {
    display: flex;
    flex-shrink: 0;
    padding: 0 16px;
    border-bottom: 1px solid $color-gray400;

    .tab-text {
      display: flex;
      align-items: center;
      gap: 2px;
    }
  }

  .search-tab,
  .favorites-tab,
  .favorites-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .search-tab,
  .favorites-tab {
    .header {
      display: flex;
      flex-shrink: 0;
      flex-direction: column;
      gap: 12px;
      padding: 12px 16px;
      color: $color-gray800;
      border-bottom: 1px solid $color-gray400;
      .full-bleed {
        width: auto;
      }
    }
    .filters {
      display: flex;
      gap: 32px;
      color: $color-gray600;

      .group,
      .order {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .themes-list {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-height: 0;
      overflow-y: scroll;
    }

    .footer {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: space-between;
      padding: 12px 24px;
      color: $color-gray800;
      border-top: 1px solid $color-gray400;

      .selected-count {
        display: flex;
        gap: 4px;
      }
    }
  }

  .favorites-tab {
    .footer .message {
      display: flex;
      align-items: center;
      color: $color-gray600;

      .icon-box {
        display: inline-flex;
        color: $color-yl7;
      }
    }

    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px;

      .icon {
        box-sizing: border-box;
        width: 48px;
        height: 48px;
        padding: 12px;
        margin: 0 auto;
        border-radius: 50%;
        color: $color-gray600;
        background: $color-gray200;
      }

      .message {
        display: flex;
        flex-direction: column;
        align-items: center;
        color: $color-gray800;
      }
    }
  }
}
</style>
